import * as React from 'react';

import MixedSelectDropdown from 'components/inputs/mixed-select-dropdown/MixedSelectDropdown';
import Input from 'components/inputs/input/Input';
import CheckboxesList from 'components/inputs/checkboxes-list/CheckboxesList';
import SVGDynamicIcon from 'components/svg-dynamic-icon/SVGDynamicIcon';
import ActionButton from 'components/buttons/action-button/ActionButton';

import { __t, applyTranslations } from 'utils/language';

import './influencers-filter.scss';

applyTranslations(require('./influencers-filters.i18n.json'));

import { Data, MixedSelectDropdownItem, TextEntry } from 'types';

export type InfluencerAgencyProfileQuery = {
  name?: string | null,
  statuses: Array<string>,
  sort: string | null,
  itemsPerPage?: number,
  currentPage?: number
};

const SORT_BY_OPTIONS = ['name_asc', 'name_desc', 'registered_at_desc', 'ongoing_campaigns_desc',
  'offered_campaigns_desc', 'followers_desc', 'followers_asc', 'engagement_rate_asc',
  'engagement_rate_desc'];

export const FILTER_BY_PROFILE_STATUS = ['approved', 'pending', 'rejected'];

interface Props {
  onUpdate: (filters) => void,
  onAddProfile: () => void,
  filterOptions: InfluencerAgencyProfileQuery
}

const InfluencersFilter = ({ filterOptions, onUpdate, onAddProfile }: Props) => {
  const { sort, name, statuses = [] } = filterOptions;
  const [searchByName, setSearchByName] = React.useState(name);

  const handleSortChange = (
    updatedItems: Array<MixedSelectDropdownItem>,
    data?: Data | null | undefined,
    selectedItem?: Partial<MixedSelectDropdownItem> | null) => {
    onUpdate({
      ...filterOptions,
      sort: selectedItem?.value || sort
    });
  };

  const handleNameChange = () => {
    onUpdate({
      ...filterOptions,
      name: searchByName
    });
  };

  const handleCheckboxChanged = (event: React.SyntheticEvent<HTMLElement>, item): void => {
    event.stopPropagation();
    event.preventDefault();

    if (item.isDisabled) {
      return;
    }

    if (!item.isSelected) {
      onUpdate({
        ...filterOptions,
        statuses: [...statuses, item.id]
      });
    } else {
      onUpdate({
        ...filterOptions,
        statuses: statuses.filter(status => status !== item.id)
      });
    }
  };

  const handleEnterPress = (e: React.KeyboardEvent<TextEntry>): void => {
    if (e.key !== 'Enter') {
      return;
    }

    handleNameChange();
  };

  return (
    <div className="influencers-filter__container">
      <div className="influencers-filter__set">
        <section className="influencers-filter__sort">
          <span className="influencers-filter__label">
            {__t('influencers_filters.sort_by.label')}
          </span>
          <MixedSelectDropdown
            name="influencers-filter-sort-dropdown"
            items={SORT_BY_OPTIONS.map(sortValue => ({
              identifier: sortValue,
              isDisabled: false,
              isSelected: sortValue === sort,
              label: __t(`influencers_filters.sort_by.${sortValue}`),
              value: sortValue
            }))}
            withSelectedInList
            className="influencers-filter-sort__dropdown"
            onItemsToggle={handleSortChange}
          />
        </section>
        <section className="influencers-filter__search-by-name-wrapper">
          <Input
            name="searchByName"
            value={searchByName || ''}
            autoComplete="off"
            placeholder={__t('influencers_filters.search_by_name.placeholder')}
            onChange={(e) => setSearchByName(e.target.value)}
            onKeyDown={handleEnterPress}
          />
          <div className="influencers-filter__search-by-name-cross">
            <SVGDynamicIcon
              name="magnifying-glass-custom-fg"
              width={15}
              height={16}
              customColor="grey"
              clickable
              onClick={() => handleNameChange()}
            />
          </div>
        </section>
      </div>
      <div className="influencers-filter__set influencers-filter__set--with-btn">
        <section className="influencers-filter__profile-status">
          <div className="influencers-filter__label">
            {__t('influencers_filters.profile_status.label')}
          </div>
          <CheckboxesList
            lists={FILTER_BY_PROFILE_STATUS.map(profileStatus => ({
              id: profileStatus,
              label: __t(`influencers_filters.profile_status.${profileStatus}`),
              isSelected: statuses.includes(profileStatus)
            }))}
            emptyLabel="Empty label"
            size="normal"
            isHorizontalList
            onCheckboxChange={handleCheckboxChanged}
          />
        </section>
        <ActionButton
          color="primary"
          onClick={onAddProfile}
          className="influencers-filter__action-button"
        >
          <SVGDynamicIcon
            name="plus-custom-fg"
            width="8px"
            height="8px"
            customColor="white"
          />
          <span>{__t('influencers_filters.add_profile_btn')}</span>
        </ActionButton>
      </div>
    </div>
  );
};

export default InfluencersFilter;
